import { Web3Provider } from '@ethersproject/providers'
import { ChainId } from '@pancakeswap-libs/sdk'
import { connectorLocalStorageKey } from '@pancakeswap-libs/uikit'
import { useWeb3React as useWeb3ReactCore } from '@web3-react/core'
// eslint-disable-next-line import/no-unresolved
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { SUPPORTED_CHAIN_IDS, injected } from '../connectors'
import { Eip6963Connector } from '../connectors/Eip6963Connector'
import { findEip6963ProviderByRdns, startEip6963Discovery, subscribeToEip6963 } from '../connectors/eip6963'
import { getLastWalletId } from '../connectors/lastWallet'
import { NetworkContextName } from '../constants'

export function useActiveWeb3React(): Web3ReactContextInterface<Web3Provider> & { chainId?: ChainId } {
  const context = useWeb3ReactCore<Web3Provider>()
  const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName)
  return context.active ? context : contextNetwork
}

export function useEagerConnect() {
  const { activate, active } = useWeb3ReactCore() // specifically using useWeb3ReactCore because of what this hook does
  const [tried, setTried] = useState(false)

  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | undefined
    let timer: ReturnType<typeof setTimeout> | undefined

    const finish = () => {
      if (!cancelled) setTried(true)
    }

    const tryActivate = (connector: Parameters<typeof activate>[0]) => {
      activate(connector, undefined, true).catch(finish)
    }

    const run = async () => {
      const hasSignedIn = window.localStorage.getItem(connectorLocalStorageKey)
      if (!hasSignedIn) {
        finish()
        return
      }

      const lastWalletId = getLastWalletId()

      // A remembered EIP-6963 wallet is reconnected specifically, rather than through
      // the shared `window.ethereum` slot - otherwise a user who chose Rabby or OKX
      // would silently come back connected to whichever extension injected last.
      if (lastWalletId && lastWalletId.includes('.')) {
        startEip6963Discovery()

        const activateByRdns = () => {
          const detail = findEip6963ProviderByRdns(lastWalletId)
          if (!detail || cancelled) return false
          tryActivate(new Eip6963Connector({ detail, supportedChainIds: SUPPORTED_CHAIN_IDS }))
          return true
        }

        if (activateByRdns()) return

        // Wallets announce asynchronously, so wait briefly for the one we want before
        // giving up and letting the user connect manually.
        unsubscribe = subscribeToEip6963(() => {
          if (activateByRdns()) unsubscribe?.()
        })
        timer = setTimeout(() => {
          unsubscribe?.()
          finish()
        }, 2000)
        return
      }

      const isAuthorized = await injected.isAuthorized()
      if (isAuthorized || (isMobile && window.ethereum)) {
        tryActivate(injected)
      } else {
        finish()
      }
    }

    run()

    return () => {
      cancelled = true
      unsubscribe?.()
      if (timer) clearTimeout(timer)
    }
  }, [activate]) // intentionally only running on mount (make sure it's only mounted once :))

  // if the connection worked, wait until we get confirmation of that to flip the flag
  useEffect(() => {
    if (active) {
      setTried(true)
    }
  }, [active])

  return tried
}

/**
 * Use for network and injected - logs user in
 * and out after checking what network theyre on
 */
export function useInactiveListener(suppress = false) {
  const { active, error, activate } = useWeb3ReactCore() // specifically using useWeb3React because of what this hook does

  useEffect(() => {
    const { ethereum } = window

    if (ethereum && ethereum.on && !active && !error && !suppress) {
      const handleChainChanged = () => {
        // eat errors
        activate(injected, undefined, true).catch((e) => {
          console.error('Failed to activate after chain changed', e)
        })
      }

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length > 0) {
          // eat errors
          activate(injected, undefined, true).catch((e) => {
            console.error('Failed to activate after accounts changed', e)
          })
        }
      }

      ethereum.on('chainChanged', handleChainChanged)
      ethereum.on('accountsChanged', handleAccountsChanged)

      return () => {
        if (ethereum.removeListener) {
          ethereum.removeListener('chainChanged', handleChainChanged)
          ethereum.removeListener('accountsChanged', handleAccountsChanged)
        }
      }
    }
    return undefined
  }, [active, error, suppress, activate])
}
